import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from './Modal';
import { tenantApi, NoSayListResponse } from '../lib/tenant-api';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface AddNoSayModalProps {
  isOpen: boolean;
  onClose: () => void;

  noSay?: NoSayListResponse | null;
  onSuccess: () => void;
}

const AddNoSayModal: React.FC<AddNoSayModalProps> = ({
  isOpen,
  onClose,
  noSay,
  onSuccess,
}) => {
  const [phrase, setPhrase] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!noSay;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && noSay) {
        setPhrase(noSay.no_say);
      } else {
        setPhrase('');
      }
    }
  }, [noSay, isEditMode, isOpen]);

  const addNoSayMutation = useMutation({
    mutationFn: tenantApi.createNoSayList,
    onSuccess: () => {
      toast.success(`No-Say phrase added successfully!`);
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to add No-Say phrase: ${error.response.data.detail}`);
    },
  });

  const updateNoSayMutation = useMutation({
    mutationFn: ({ noSay, noSayId }: { noSay: string; noSayId: string }) =>
      tenantApi.updateNoSayList(noSay, noSayId),
    onSuccess: () => {
      toast.success(`No-Say phrase updated successfully!`);
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to update No-Say phrase: ${error.response.data.detail}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phrase.trim()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (isEditMode && noSay) {
        await updateNoSayMutation.mutateAsync({
          noSay: phrase,
          noSayId: noSay.id,
        });
      } else {
        await addNoSayMutation.mutateAsync(phrase);
      }
    } catch (err) {
      setError('Failed to save the phrase. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPhrase('');
    setError(null);
    setIsLoading(false);
    onClose();
  };

  return (
    <Modal size='sm' isOpen={isOpen} onClose={handleClose} title={isEditMode ? 'Edit No Say Phrase' : 'Add No Say Phrase'}>
      <form onSubmit={handleSubmit}>
        <div className="">
          <div className="mb-4">
            <label htmlFor="phrase" className="block text-sm font-medium text-gray-700 mb-1">
              No-Say Word or Phrase
            </label>
            <input
              type="text"
              id="phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter a word or phrase"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        </div>
        <footer className=" py-3  sm:flex sm:flex-row-reverse">
          <button
            type="submit"
            disabled={isLoading || !phrase.trim()}
            className="w-full inline-flex justify-center  border border-transparent shadow-sm px-4 py-2  bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="animate-spin h-5 w-5" /> : (isEditMode ? 'Save' : 'Add')}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="mt-3 w-full inline-flex justify-center  border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </footer>
      </form>
    </Modal>
  );
};

export default AddNoSayModal;
